import update from "immutability-helper";
import moment from "moment";
import React from "react";
import { Modal } from "../../../components";
import { validateSegment } from "../../flightvalidator";
import { type CommercialFlightEditProps, type FlightEditProps, type FlightEditState, type FlightSegmentsEdit, getDefaultSegment, type PrivateFlightEditProps, type Segment } from "../../model";
import GeneralFlightEdit from "../flightdetailsedit/generalflightedit";
import RetrieveFlights from "../retrieveflights/retrieveflights";
import SegmentEdit from "../segmentedit/segmentedit";
import EditViewPanel from "components/panels/EditViewPanel";
import { ButtonSecondary } from "components/Atoms/Button";

interface Props {
  onFlightCreated: (flight: server.dto.Flight) => void
  frequentFlyerPrograms: server.dto.FrequentFlyerProgram[]
  onModalClose: (flightEditState: FlightEditState) => void

  initialArtist?: server.dto.ArtistSmall
  initialDate?: Date

  lastFlightState: FlightEditState
}

interface State {
  segments: FlightSegmentsEdit
  flight: FlightEditProps

  showFlightlookup: boolean
  noArtistError: boolean
}

const defaultFlight = (): FlightEditProps => ({
  phase: "confirmed",
  artist: null,
  travellers: null,
  bookingCode: "",
  travelAgency: "",
  notes: ""
});

const shouldStartNewState = (props: Props) => {
  if (props.lastFlightState == null) { return true; }

  if (props.initialDate?.valueOf() !== props.lastFlightState.initialDate?.valueOf()) { return true; }

  if (props.initialArtist?.artistId !== props.lastFlightState.initialArtist?.artistId) { return true; }

  return false;
};

export default class InitialFlightCreate extends React.Component<Props, State> {
  state: State = shouldStartNewState(this.props)
    ? {
      segments: {},
      showFlightlookup: true,
      flight: { ...defaultFlight(), artist: this.props.initialArtist },
      noArtistError: false
    }
    : {
      segments: this.props.lastFlightState.segments,
      showFlightlookup: false,
      flight: this.props.lastFlightState.flight,
      noArtistError: false
    };

  async componentDidMount() {
    if (this.props.initialArtist && shouldStartNewState(this.props)) {
      const instruction: server.dto.ListContactByCompanyInstruction = {
        companyId: this.props.initialArtist.artistId,
        page: 1,
        perPage: 1000,
        defaultSelect: true
      };

      const result = await window.CoreApi.CompanyToContact.ListContactByCompany(instruction);

      const defaultSelectedTravellers = result
        .data
        .data
        .map(item => (item.contact));

      this.setState({
        flight: {
          ...this.state.flight,
          travellers: defaultSelectedTravellers
        }
      });
    }
  }

  validate = (): boolean => {
    const keys = Object.keys(this.state.segments);

    let anyErrors = false;

    const result = keys.reduce<any>((acc: Record<number, Segment>, current: string): Record<number, Segment> => {
      const segment = this.state.segments[Number(current)];

      const validationResult = validateSegment(segment);

      anyErrors = anyErrors || validationResult.anyErrors();

      return {
        ...acc,
        [Number(current)]: {
          ...segment,
          ...validationResult.errors
        }
      };
    }, {});

    const noArtistError = this.state.flight.artist == null;

    this.setState({ segments: result, noArtistError });

    return anyErrors || noArtistError;
  };

  onTypeChange = (newType: server.dto.FlightTypes, index: number) => {
    const newState = update(this.state, {
      segments: {
        [index]: {
          type: { $set: newType }
        }
      }
    });

    this.setState(newState);
  };

  onSegmentEdited = (segment: CommercialFlightEditProps & PrivateFlightEditProps, index: number) => {
    const newState = update(this.state, {
      segments: {
        [index]: {
          props: { $set: segment }
        }
      }
    });
    this.setState(newState);
  };

  getNewIndex = (): number => {
    const ids = Object.keys(this.state.segments);
    return ids.length;
  };

  onManualEntry = () => {
    const initialDate = this.props.initialDate ? moment.utc(this.props.initialDate) : moment.utc();
    const newState = update(this.state, {
      segments: {
        [this.getNewIndex()]: {
          $set: {
            props: { ...getDefaultSegment(), departure: initialDate, arrival: initialDate },
            type: "commercial",
            noDepartureAirportError: false,
            noArrivalAirportError: false,
            noAirlineError: false,
            noFlightCodeError: false,
            noFlightNumberError: false,
            departureTooEarly: false
          }
        }
      },
      showFlightlookup: { $set: false }
    });

    this.setState(newState);
  };

  onExistingFlightEntry = (segments: server.dto.FlightSegment[]) => {
    const lastIndex = this.getNewIndex();

    const newSegments = segments.map(s => ({
      type: "commercial",
      props: {
        arrival: moment.utc(s.arrival.dateTimeLocal),
        departure: moment.utc(s.departure.dateTimeLocal),

        arrivalAirport: s.arrival.airport,
        departureAirport: s.departure.airport,

        arrivalTerminal: s.arrival.terminal || "",
        departureTerminal: s.departure.terminal || "",

        arrivalGate: s.arrival.gate || "",
        departureGate: s.departure.gate || "",

        airline: s.airline,

        flightNumber: s.flightDesignator.number.toString() || "",
        flightCode: s.flightDesignator.code || "",

        aircraft: s.aircraft || "",

        seats: s.seats || "",
        travelClass: s.travelClass,
        eticket: s.eTicket || "",

        access: "",
        confirmationNumber: "",
        capacity: ""
      }
    }))
      .reduce((acc, curr, idx) =>
        ({ ...acc, [idx + lastIndex]: curr }), {});// merge acc with the new obj, e.g. {1: {}} can become {1: {}, 2: {}}

    this.setState({ segments: { ...this.state.segments, ...newSegments }, showFlightlookup: false });
  };

  onAddSegmentClick = () => {
    this.setState({ showFlightlookup: true });
  };

  onSaveClick = async () => {
    if (this.validate()) {
      return;
    }

    const flight: server.dto.Flight = {
      phase: this.state.flight.phase,
      artist: this.state.flight.artist,
      travellers: this.state.flight.travellers,
      bookingCode: this.state.flight.bookingCode,
      travelAgency: this.state.flight.travelAgency,
      notes: this.state.flight.notes,

      segments: Object.keys(this.state.segments).map(key => {
        const item = this.state.segments[Number(key)];
        const { props } = item;
        return {
          id: 0,
          departure: {
            dateTimeLocal: props.departure.toDate(),
            airport: props.departureAirport,
            terminal: props.departureTerminal,
            gate: props.departureGate
          },
          arrival: {
            dateTimeLocal: props.arrival.toDate(),
            airport: props.arrivalAirport,
            terminal: props.arrivalTerminal,
            gate: props.arrivalGate
          },
          type: item.type,
          flightDesignator: {
            code: props.flightCode,
            number: Number(props.flightNumber)
          },
          eTicket: props.eticket,
          airline: props.airline,
          flightNumber: props.flightNumber,
          aircraft: props.aircraft,
          travelClass: props.travelClass,
          seats: props.seats,
          frequentFlyerProgram: props.frequentFlyer,
          capacity: {
            value: props.capacity,
            formatted: ""
          },
          confirmationNumber: "",
          access: "",
          linkedShows: []
        } as any;
      })
    } as any;

    const result = await window.Api.Flights.CreateFlight(flight);

    this.props.onFlightCreated(result.data);
  };

  public render(): JSX.Element {
    const headerButton = site.permissions.flight.canCreate
      ? <ButtonSecondary onClick={this.onAddSegmentClick} label={RESX.Flight.AddSegment} />
      : null;

    return <>
      <Modal
        handleClose={() => {
          this.props.onModalClose({
            flight: this.state.flight,
            segments: this.state.segments,
            initialArtist: this.props.initialArtist,
            initialDate: this.props.initialDate
          });
        }}
        title={RESX.Flight.resxFlight}
        isOpen
        hasAutoScrollBox
        width={"large"}
        height={800}
        headerButton={headerButton}
      >
        <EditViewPanel
          className="grow1 scrollbox-auto"
          title={RESX.Flight.resxFlight} >
          <GeneralFlightEdit
            flight={this.state.flight}
            onFlightEdit={flight => { this.setState({ flight }); }}
            noArtistError={this.state.noArtistError}
          />

          {Object.values(this.state.segments).map((segment, idx) => (
            <SegmentEdit
              key={idx}
              editedSegment={segment.props}
              onSegmentEdited={this.onSegmentEdited}
              index={idx}
              type={segment.type}
              onTypeChanged={this.onTypeChange}

              frequentFlyerPrograms={this.props.frequentFlyerPrograms}

              noAirlineError={segment.noAirlineError}
              noArrivalAirportError={segment.noArrivalAirportError}
              noDepartureAirportError={segment.noDepartureAirportError}
              noFlightCodeError={segment.noFlightCodeError}
              noFlightNumberError={segment.noFlightNumberError}
              departureTooEarly={segment.departureTooEarly}
            />
          ))}
        </EditViewPanel>

        <div className="flex margin-top-large justify-end">
          <div>
            <button type="button" onClick={this.onSaveClick} className="button-new primary" value={RESX.Buttons.btnSave} name="Save">
              {RESX.Buttons.btnSave}
            </button>
          </div>
        </div>
      </Modal>

      {this.state.showFlightlookup && (
        <Modal
          title={RESX.Flight.FlightLookup}
          width={"large"}
          minHeight={450}
          height={600}
          hasAutoScrollBox
          isOpen={this.state.showFlightlookup}
          handleClose={() => { this.setState({ showFlightlookup: false }); }}
        >
          <RetrieveFlights
            onManualEntryClicked={this.onManualEntry}
            onSelectWithSegmentClicked={this.onExistingFlightEntry}
            initialDate={this.props.initialDate}
          />
        </Modal>
      )
      }
    </>;
  }
}
